import {
  Button,
  Divider,
  Form,
  Input,
  InputNumber,
  Space,
  Typography,
  notification,
} from '@pankod/refine-antd';
import { useCan } from '@pankod/refine-core';
import { useEffect, useState } from 'react';
import { ISaleProductState } from 'reducers/product-sale-reducer';
import { InventoryLotWithVariant, getInventoryLotsByVariant } from 'services/inventory';
import {
  EDiscountEntryType,
  calculateSaleLineDiscountCost,
  getSaleLineDiscountEntryError,
  getUnitDiscountEntryForEdit,
} from 'utilities/discount-entry';
import styles from './style.module.css';
import { UnitDiscountFormItem } from './UnitDiscountFormItem';

type EditProductFormProps = {
  editProduct: ISaleProductState;
  warehouse: string | undefined;
  onSubmit: (editedProduct: ISaleProductState) => void;
};

const { Text } = Typography;

export const EditProductForm: React.FC<EditProductFormProps> = (props) => {
  const [inventoryLots, setInventoryLots] = useState<InventoryLotWithVariant[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [form] = Form.useForm();
  const { data: salesPriceDecrease } = useCan({
    resource: 'sales_price',
    action: 'decrease',
  });
  const { data: salesPriceIncrease } = useCan({
    resource: 'sales_price',
    action: 'change',
  });

  useEffect(() => {
    form.resetFields();
    setInventoryLots([]);

    (async () => {
      setIsLoading(true);
      try {
        if (!props.warehouse || !props.editProduct) return;

        let quantityString = '';
        const inventoryLotResponse = await getInventoryLotsByVariant(
          props.editProduct.productVariantId,
          props.warehouse,
        );
        setInventoryLots(inventoryLotResponse);

        form.setFieldsValue({
          variant: `${props.editProduct.sku} - ${props.editProduct.productVariantName} ${quantityString}`,
        });
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
      setIsLoading(false);
    })();
  }, [props.editProduct, form, props.warehouse]);

  const onFinish = (values: ISaleProductState) => {
    const discountEntryValue = values.discountEntryValue || null;
    const discountEntryType: EDiscountEntryType | null = discountEntryValue
      ? (values.discountEntryType ?? EDiscountEntryType.AmountPerUnit)
      : null;
    const newData = {
      ...props.editProduct,
      quantity: values.quantity,
      unitPrice: values.unitPrice,
      // Recomputed from the stored entry, so a quantity change keeps 10% or TK 50 per unit.
      discountCost: calculateSaleLineDiscountCost(
        values.quantity,
        values.unitPrice,
        discountEntryValue,
        discountEntryType,
      ),
      discountEntryValue,
      discountEntryType,
    };

    const totalQuantity = inventoryLots.reduce((total, lot) => {
      total += lot.quantity;
      return total;
    }, 0);

    if (newData.quantity > totalQuantity || newData.quantity === 0) {
      form.setFields([
        {
          name: 'quantity',
          errors: [`Quantity needs to be between 1 and ${totalQuantity}`],
        },
      ]);
      return;
    }

    const discountEntryError = getSaleLineDiscountEntryError(
      newData.unitPrice,
      discountEntryValue,
      discountEntryType,
    );
    if (discountEntryError) {
      form.setFields([{ name: 'discountEntryValue', errors: [discountEntryError] }]);
      return;
    }

    // Increase & Decrease the price when have both permissions otherwise
    const canIncreasePrice = salesPriceIncrease?.can;
    const canDecreasePrice = salesPriceDecrease?.can;
    if (canIncreasePrice === false) {
      const isUnitPriceIncreased = values.unitPrice > props.editProduct.unitPrice;
      // Only increase permission can higher the price
      if (isUnitPriceIncreased) {
        form.setFields([
          {
            name: 'unitPrice',
            errors: ['Price cannot be higher than Sales Price'],
          },
        ]);
        return;
      }
    }

    if (canDecreasePrice === false) {
      const isUnitPriceDecreased = values.unitPrice < props.editProduct.unitPrice;
      // Only decrease permission can lower the price
      if (isUnitPriceDecreased) {
        form.setFields([
          {
            name: 'unitPrice',
            errors: ['Price cannot be lower than Sales Price'],
          },
        ]);
        return;
      }
    }

    props.onSubmit(newData);
    form.resetFields();
    setInventoryLots([]);
  };

  const availableStock = () => {
    return (
      <ul>
        {inventoryLots.map((lot) => (
          <li key={lot.id}>
            <Text>
              {lot.quantity} available at BDT {lot.unitPrice}
            </Text>
          </li>
        ))}
      </ul>
    );
  };

  const attributes: {
    attributeId: string;
    attributeName: string;
    attributeType: 'text' | 'float' | 'boolean' | 'dropdown';
    value: string;
  }[] = [];
  const templates = props.editProduct.productTemplates || [];
  templates.forEach((template) => {
    const { productTemplateAttributes } = template;
    attributes.push(...productTemplateAttributes);
  });

  const customFields = props.editProduct.customFields ?? [];

  const attributeElements = attributes.map((attribute) => (
    <div
      key={attribute.attributeId}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
    >
      <Typography.Text strong>{attribute.attributeName.toUpperCase()} : </Typography.Text>
      <Typography.Text>{attribute.value}</Typography.Text>
    </div>
  ));

  const customFieldElements = customFields.map((field, idx) => {
    const key = Object.keys(field)[0];
    const value = field[key];

    return (
      <div
        key={idx}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Typography.Text strong>{key.toUpperCase()} : </Typography.Text>
        <Typography.Text>{value}</Typography.Text>
      </div>
    );
  });

  return (
    <Form
      layout="vertical"
      initialValues={{
        variant: `${props.editProduct.sku} - ${props.editProduct.productVariantName}`,
        quantity: props.editProduct.quantity,
        unitPrice: props.editProduct.unitPrice,
        ...getUnitDiscountEntryForEdit(props.editProduct),
      }}
      form={form}
      onFinish={onFinish}
    >
      <Form.Item label="Product" name="variant">
        <Input disabled />
      </Form.Item>

      <Divider />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography.Text strong>PARENT PRODUCT:</Typography.Text>
          <Typography.Text code>{props.editProduct.productName}</Typography.Text>
        </div>

        {attributeElements}
        {customFieldElements}

        {/* <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography.Text strong>DEFAULT SELLING PRICE:</Typography.Text>
          <Typography.Text>{props.editProduct.sellingPrice}</Typography.Text>
        </div> */}
      </div>
      <Divider />

      <Form.Item
        label="Unit Price"
        name="unitPrice"
        rules={[{ required: true }, { type: 'number', min: 0 }]}
      >
        <InputNumber style={{ width: '100%' }} precision={2} />
      </Form.Item>
      <UnitDiscountFormItem />
      <Form.Item
        label="Quantity"
        name="quantity"
        rules={[
          {
            required: true,
            type: 'integer',
            min: 1,
            message: 'Quantity must be a positive integer value',
          },
        ]}
      >
        <InputNumber style={{ width: '100%' }} />
      </Form.Item>

      <Space direction="vertical">
        <Text className={styles['stock__text']}>* Available Stock</Text>
        {availableStock()}
      </Space>

      <Form.Item wrapperCol={{ span: 4, offset: 20 }}>
        <Button type="primary" htmlType="submit" loading={isLoading} disabled={isLoading}>
          Save
        </Button>
      </Form.Item>
    </Form>
  );
};
